import { useMemo, useRef } from 'react'
import { useSpring, config } from '@react-spring/web'
import { ResumeFieldSuggest } from 'builder/modules/resumeEditor'
import { useI18n } from 'builder/hooks/useI18n'
import type { ToneName } from 'builder/styles/colors'
import { ResumeWritingButton, useResumeWriting } from '../ResumeWritingBanner'
import {
  Container,
  Header,
  HeaderMain,
  HeaderAside,
  Percent,
  Progress,
  ProgressInner,
  HeaderText,
  Suggestion,
  PercentSuggestion,
} from './styles'

type Props = {
  handleSuggestionClick?: (sectionId: ResumeFieldSuggest) => void
  suggestion?: ResumeFieldSuggest
  points: number
  isHeaderFullBorderRadius?: boolean
  top?: number
  className?: string
}

export const ResumeScoreContainer = (props: Props) => {
  const { suggestion, points, handleSuggestionClick, isHeaderFullBorderRadius, top, className } =
    props
  const containerRef = useRef<HTMLDivElement | null>(null)
  const { i18n } = useI18n()
  const { isButtonVisible: isResumeWritingButtonVisible } = useResumeWriting()
  const [barColor] = useMemo(() => {
    const getColorByPoints = (points: number): ToneName => {
      if (points > 70) return 'green'
      if (points > 30) return 'amber'
      return 'red'
    }

    const color = getColorByPoints(points)
    return [`var(--${color}-40)`, `var(--${color}-50)`]
  }, [points])

  const springProps = useSpring({
    config: config.slow,
    from: { points: 0 },
    to: { points: Math.min(points, 100) },
  })

  return (
    <Container
      ref={containerRef}
      $top={top}
      $isHeaderFullBorderRadius={isHeaderFullBorderRadius}
      className={className}
    >
      <Header>
        <HeaderMain>
          <Percent points={points}>{springProps.points.to(value => value.toFixed(0))}</Percent>
          <HeaderText>{i18n.t('builder.resume_tuner.banner.dashboard_score')}</HeaderText>
        </HeaderMain>

        {!!suggestion?.points && (
          <HeaderAside>
            <Suggestion onClick={() => handleSuggestionClick?.(suggestion)}>
              <PercentSuggestion>+{suggestion.points}</PercentSuggestion>
              {suggestion.text}
            </Suggestion>
            {isResumeWritingButtonVisible && <ResumeWritingButton />}
          </HeaderAside>
        )}
      </Header>
      <Progress>
        <ProgressInner
          style={{
            transform: springProps.points.to(value => `scaleX(${value / 100})`),
            backgroundColor: barColor,
          }}
        />
      </Progress>
    </Container>
  )
}
